'use client'

import { useState } from 'react'
import Link from 'next/link'
import { useQuery } from '@tanstack/react-query'
import { Bot, Package, Sparkles, GitFork, Play } from 'lucide-react'
import {
  getPublicAgentsForUser,
  getPublicProjectsForUser,
  getPublicSkillsForUser,
} from '../_actions/get-public-work'
import { PromptsPublicView } from '../prompts/_components/prompts-public-view'
import { FEATURES } from '@/lib/config/features'

type SubTab = 'agents' | 'skills' | 'projects' | 'prompts'

const ALL_SUBTABS: { id: SubTab; label: string }[] = [
  { id: 'agents', label: 'Agents' },
  { id: 'skills', label: 'Skills' },
  { id: 'projects', label: 'Projects' },
  { id: 'prompts', label: 'Prompts' },
]

const SUBTABS = FEATURES.skills
  ? ALL_SUBTABS
  : ALL_SUBTABS.filter((t) => t.id !== 'skills')

interface MyWorkTabsProps {
  targetUserId: string
  username: string
  displayName: string | null
  avatarUrl: string | null
  isOwnProfile: boolean
}

function EmptyState({ icon: Icon, title, hint }: { icon: typeof Bot; title: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-[var(--color-border-default)] bg-[var(--color-bg-surface)] py-14 text-center">
      <Icon className="mb-3 h-9 w-9 text-[var(--color-text-muted)]" aria-hidden="true" />
      <p className="text-sm font-semibold text-[var(--color-text-primary)]">{title}</p>
      {hint && <p className="mt-1 max-w-xs text-xs text-[var(--color-text-muted)]">{hint}</p>}
    </div>
  )
}

function GridSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="h-24 animate-pulse rounded-xl bg-[var(--color-bg-elevated)]" />
      ))}
    </div>
  )
}

function WorkCard({
  href, badge, BadgeIcon, title, description, stats,
}: {
  href: string
  badge: string
  BadgeIcon: typeof Bot
  title: string
  description?: string | null
  stats?: { Icon: typeof Play; label: string }[]
}) {
  return (
    <Link
      href={href}
      className="block rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-4 transition-[border-color] hover:border-[var(--color-brand-indigo)]"
    >
      <span className="inline-flex items-center gap-1 rounded-full bg-[var(--color-bg-elevated)] px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-[var(--color-text-secondary)]">
        <BadgeIcon className="h-3 w-3" aria-hidden="true" />
        {badge}
      </span>
      <div className="mt-2 truncate text-sm font-semibold text-[var(--color-text-primary)]">{title}</div>
      {description && (
        <p className="mt-1 line-clamp-2 text-xs text-[var(--color-text-muted)]">{description}</p>
      )}
      {stats && stats.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-3 border-t border-[var(--color-border-default)] pt-2 text-xs text-[var(--color-text-muted)]">
          {stats.map((s, i) => (
            <span key={i} className="flex items-center gap-1">
              <s.Icon className="h-3 w-3" aria-hidden="true" />
              <span className="tabular-nums">{s.label}</span>
            </span>
          ))}
        </div>
      )}
    </Link>
  )
}

function AgentsPanel({ userId, isOwn }: { userId: string; isOwn: boolean }) {
  const { data, isLoading } = useQuery({
    queryKey: ['public-agents', userId],
    queryFn: () => getPublicAgentsForUser(userId),
    staleTime: 60_000,
    retry: 1,
  })
  if (isLoading) return <GridSkeleton />
  if (!data || data.length === 0) {
    return (
      <EmptyState
        icon={Bot}
        title="No public agents yet"
        hint={isOwn ? 'Publish an agent and it shows up here as proof of your work.' : 'This prompter has no public agents yet.'}
      />
    )
  }
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {data.map((a) => (
        <WorkCard
          key={a.id}
          href={`/agents/${a.slug}`}
          badge="Agent"
          BadgeIcon={Bot}
          title={a.name}
          description={a.description}
          stats={[
            { Icon: Play, label: `${a.useCount} runs` },
            { Icon: GitFork, label: `${a.forkCount} forks` },
          ]}
        />
      ))}
    </div>
  )
}

function SkillsPanel({ userId, username, isOwn }: { userId: string; username: string; isOwn: boolean }) {
  const { data, isLoading } = useQuery({
    queryKey: ['public-skills', userId],
    queryFn: () => getPublicSkillsForUser(userId),
    staleTime: 60_000,
    retry: 1,
  })
  if (isLoading) return <GridSkeleton />
  if (!data || data.length === 0) {
    return (
      <EmptyState
        icon={Sparkles}
        title="No public skills yet"
        hint={isOwn ? 'Publish a skill and it shows up here as proof of your work.' : 'This prompter has no public skills yet.'}
      />
    )
  }
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {data.map((s) => (
        <WorkCard
          key={s.id}
          href={`/skills/${s.slug}?by=${encodeURIComponent(username)}`}
          badge="Skill"
          BadgeIcon={Sparkles}
          title={s.name}
          description={s.description}
          stats={[
            { Icon: Play, label: `${s.useCount} installs` },
            { Icon: GitFork, label: `${s.forkCount} forks` },
          ]}
        />
      ))}
    </div>
  )
}

function ProjectsPanel({ userId, isOwn }: { userId: string; isOwn: boolean }) {
  const { data, isLoading } = useQuery({
    queryKey: ['public-projects', userId],
    queryFn: () => getPublicProjectsForUser(userId),
    staleTime: 60_000,
    retry: 1,
  })
  if (isLoading) return <GridSkeleton />
  if (!data || data.length === 0) {
    return (
      <EmptyState
        icon={Package}
        title="No public projects yet"
        hint={isOwn ? 'Bundle agents into a project and make it public to feature it here.' : 'This prompter has no public projects yet.'}
      />
    )
  }
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      {data.map((p) => (
        <WorkCard
          key={p.id}
          href={`/projects/${p.slug}`}
          badge="Project"
          BadgeIcon={Package}
          title={p.name}
          description={p.description}
          stats={[{ Icon: Bot, label: `${p.agentCount} agents` }]}
        />
      ))}
    </div>
  )
}

export function MyWorkTabs({ targetUserId, username, displayName, avatarUrl, isOwnProfile }: MyWorkTabsProps) {
  const [sub, setSub] = useState<SubTab>('agents')

  return (
    <div>
      {/* Sub-tab chips */}
      <div role="tablist" aria-label="My work" className="mb-4 flex flex-wrap gap-1.5">
        {SUBTABS.map((t) => {
          const active = t.id === sub
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setSub(t.id)}
              className={`rounded-full border px-3 py-1 text-xs font-semibold transition-colors ${
                active
                  ? 'border-[var(--color-brand-indigo)] bg-[var(--color-brand-indigo)]/10 text-[var(--color-brand-indigo)]'
                  : 'border-[var(--color-border-default)] text-[var(--color-text-muted)] hover:text-[var(--color-text-secondary)]'
              }`}
            >
              {t.label}
            </button>
          )
        })}
      </div>

      {sub === 'agents' && <AgentsPanel userId={targetUserId} isOwn={isOwnProfile} />}
      {sub === 'projects' && <ProjectsPanel userId={targetUserId} isOwn={isOwnProfile} />}
      {FEATURES.skills && sub === 'skills' && (
        <SkillsPanel userId={targetUserId} username={username} isOwn={isOwnProfile} />
      )}
      {sub === 'prompts' && (
        <PromptsPublicView
          embedded
          targetUserId={targetUserId}
          username={username}
          displayName={displayName}
          avatarUrl={avatarUrl}
          isOwnProfile={isOwnProfile}
        />
      )}
    </div>
  )
}
